import React, { Component } from 'react';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLink } from 'react-router-dom';
import { find, debounce } from 'lodash';
import { toast } from 'react-toastify';

import { getUser } from 'src/user';
import {
  setBreadcrumbs,
  UiSelect,
  reduceServerErrors,
  UiToastAdapter,
  ConfirmDeletionModal,
  stripHtml,
  GuidelinesCard,
} from 'src/ui';
import {
  getAccount,
  fetchAccount,
  fetchAccountVendors,
  addAccountVendor,
  deleteAccountVendor,
} from 'src/account';
import { updateVendor } from 'src/vendor';
import { ClientSidePagingManager } from 'src/api';
import { Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';

import { getVendors } from '../redux';
import { VendorsTable } from '../components';
import { getVendorsByName } from '../services';

class VendorsListPage extends Component {
  state = {
    loading: true,
    selectedVendor: null,
    archivingVendor: null,
    accountVendors: [],
    defaultVendors: [],
  };

  guidelines = [
    `Here we will list all of the vendors that you do business with. Consider the services you receive and include the company here. Examples are Comcast for internet and PSE for electricity.`,
    `Don't forget to include companies that you receive incidental service from like IT providers and secondary suppliers.`,
    `Many of your suppliers my already be in our list, if so select them in the search options. Any new vendors will be added to our system and will be reviewed for approval. Don't worry beyond this point as we will contact you if there are any issues.`,
    `If you need assistance with uploading a very large vendor list please contact support.`,
  ];

  constructor(props) {
    super(props);

    this.pagedAccountVendors = new ClientSidePagingManager(
      this.props.fetchAccountVendors, // eslint-disable-line react/destructuring-assignment
      () => this.props.account.vendors || [], // eslint-disable-line react/destructuring-assignment
      accountVendors => {
        this.setState({ accountVendors });
      }
    );

    this.fetchVendors = this.fetchVendors.bind(this);
    this.debouncedFetchVendors = debounce(this.fetchVendors, 500);
  }

  async componentDidMount() {
    const { setBreadcrumbs } = this.props;

    setBreadcrumbs([{ label: 'Vendors', href: '/vendors' }]);

    const { user, fetchAccount } = this.props;

    if (user.accountId) {
      await fetchAccount(user.accountId);
      await this.pagedAccountVendors.fetch({ accountId: user.accountId });
    }

    const defaultVendors = await this.fetchVendorsData('');

    this.setState({ loading: false, defaultVendors });
  }

  get activeVendors() {
    const {
      account: { vendors },
    } = this.props;

    if (vendors) {
      return vendors
        .filter(vendor => !vendor.isArchived)
        .slice()
        .sort(a => {
          if (a.isApproved) {
            return -1;
          }
          if (!a.isDenied) {
            return 0;
          }
          return 1;
        });
    }
    return [];
  }

  get availableVendors() {
    const {
      account: { vendors: selectedVendors },
      vendors: { items: vendors },
    } = this.props;

    if (selectedVendors && vendors) {
      return vendors.filter(
        vendor => !find(selectedVendors, { id: vendor.id })
      );
    }
    return [];
  }

  getVendorStatus(vendor) {
    let status = 'pending';

    if (vendor.isApproved) {
      status = 'verified';
    } else if (vendor.isDenied) {
      status = 'denied';
    } else if (vendor.isArchived) {
      status = '-';
    }

    return status;
  }

  handleOnSelectVendor = value => {
    this.setState({ selectedVendor: value });
    this.addVendor(value);
  };

  addVendor = async vendor => {
    const { addAccountVendor, user } = this.props;

    this.setState({ loading: true });

    await addAccountVendor({
      accountId: user.accountId,
      vendorId: vendor.id,
    });

    const { account } = this.props;

    if (account.isError()) {
      reduceServerErrors(account);
    } else {
      this.pagedAccountVendors.refresh();
      toast(
        UiToastAdapter(
          'Vendor added to your business account successfully!',
          'success'
        )
      );
    }

    this.setState({ loading: false, selectedVendor: null });
  };

  onArchive = vendor => {
    this.setState({ archivingVendor: vendor });
  };

  onCancelArchive = () => {
    this.setState({ archivingVendor: null });
  };

  onConfirmArchive = async () => {
    const { deleteAccountVendor, user } = this.props;
    const { archivingVendor } = this.state;

    this.setState({ loading: true });
    this.onCancelArchive();

    await deleteAccountVendor({
      accountId: user.accountId,
      vendorId: archivingVendor.id,
    });

    const { account } = this.props;

    if (account.isError()) {
      reduceServerErrors(account);
    } else {
      this.pagedAccountVendors.refresh();
      toast(UiToastAdapter('Vendor archived successfully!', 'success'));
    }

    this.setState({ loading: false });
  };

  onUpdate = async vendor => {
    const { updateVendor } = this.props;
    // the row is a display copy of the vendor, so only the decision is sent
    await updateVendor({
      vendorId: vendor.id,
      isApproved: vendor.isApproved,
      isDenied: vendor.isDenied,
    });

    const { vendors } = this.props;

    if (vendors.isError()) {
      reduceServerErrors(vendors);
    } else {
      const message = `Vendor successfully ${
        vendor.isApproved ? 'approved' : 'denied'
      }!`;
      await this.pagedAccountVendors.refetch();
      toast(UiToastAdapter(message, 'success'));
    }
  };

  fetchVendorsData = async searchText => {
    const vendorsResponse = await getVendorsByName(searchText);
    if (vendorsResponse.ok) {
      const vendors = vendorsResponse.data;
      const {
        account: { vendors: selectedVendors },
      } = this.props;

      return vendors.filter(
        vendor => !find(selectedVendors || [], { id: vendor.id })
      );
    }

    return [];
  };

  async fetchVendors(searchText, callback) {
    const vendorsData = await this.fetchVendorsData(searchText);
    callback(vendorsData);
  }

  render() {
    const { user } = this.props;
    const {
      selectedVendor,
      loading,
      archivingVendor,
      accountVendors,
      defaultVendors,
    } = this.state;

    return (
      <div className="vendors">
        <Columns isMultiline>
          <Column isSize="1/2">
            <MuiTitle isSize="4" hasTextColor="info" tag="h2">
              Vendors
            </MuiTitle>
          </Column>
          <Column hasTextAlign="right" isSize="1/2">
            <NavLink
              className="button is-gradient-bright is-long"
              to="/vendors/add"
            >
              <span className="icon">
                <FontAwesomeIcon icon={['fal', 'plus']} />
              </span>
              <span>New Vendor</span>
            </NavLink>
          </Column>
          <Column>
            {/* <Box> */}
            <UiSelect
              name="addVendor"
              id="addVendor"
              defaultOptions={defaultVendors}
              label={
                <>
                  <span className="icon">
                    <FontAwesomeIcon icon={['fal', 'search']} />
                  </span>
                  <span>Search vendors</span>
                </>
              }
              loadOptions={(searchText, callback) => {
                this.debouncedFetchVendors(searchText, callback);
              }}
              getOptionValue={item => item.id}
              getOptionLabel={item => item.businessName}
              hideHelper
              onChange={this.handleOnSelectVendor}
              components={{
                Option: ({ innerProps, data }) => (
                  <div {...innerProps} className="ui-select__option">
                    <Columns
                      hasTextColor="primary"
                      className="has-text-weight-semibold"
                    >
                      <Column className="vendor-title">
                        {data.businessName}
                      </Column>
                      <Column className="vendor-type">
                        {stripHtml(data.businessEntityTypeName)}
                      </Column>
                      <Column className="vendor-status has-text-right">
                        {this.getVendorStatus(data)}
                      </Column>
                    </Columns>
                  </div>
                ),
              }}
              isLoading={loading}
              value={selectedVendor}
            />
            {/* </Box> */}
            <VendorsTable
              loading={loading}
              vendors={accountVendors}
              onArchive={this.onArchive}
              onUpdate={user.isBankAdmin ? this.onUpdate : null}
              paging={this.pagedAccountVendors}
            />
          </Column>
          {!user.isBankAdmin && (
            <Column isSize="narrow">
              <GuidelinesCard guidelines={this.guidelines} />
            </Column>
          )}
        </Columns>
        <ConfirmDeletionModal
          isActive={!!archivingVendor}
          onClickCancel={this.onCancelArchive}
          onClickConfirm={this.onConfirmArchive}
          title={archivingVendor ? archivingVendor.businessName : ''}
        />
      </div>
    );
  }
}

VendorsListPage.propTypes = {
  user: propTypes.object.isRequired,
  account: propTypes.object.isRequired,
  vendors: propTypes.object.isRequired,
  fetchAccount: propTypes.func.isRequired,
  fetchAccountVendors: propTypes.func.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  addAccountVendor: propTypes.func.isRequired,
  deleteAccountVendor: propTypes.func.isRequired,
  updateVendor: propTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    user: getUser(state),
    account: getAccount(state),
    vendors: getVendors(state),
  };
}

const connectedVendorsListPage = connect(mapStateToProps, {
  fetchAccount,
  setBreadcrumbs,
  addAccountVendor,
  deleteAccountVendor,
  updateVendor,
  fetchAccountVendors,
})(VendorsListPage);

export { connectedVendorsListPage as VendorsListPage };
